import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Undo2, Crop, RotateCw, Pencil, Type, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { App } from "@capacitor/app";
import { COLORS, FULL, loadImage, fitCanvas, rotateCanvas, cropCanvas, drawStroke, flatten, canvasToFile } from "@/lib/photoEdit";
import { ColorRow } from "./ColorRow";
import { TextInput } from "./TextInput";
import { CropBox } from "./CropBox";
import { TextItem } from "./TextItem";

const toolBtn = (active) =>
  `h-10 w-10 rounded-full flex items-center justify-center transition-transform active:scale-90 disabled:opacity-30 ${active ? "bg-white text-black" : "bg-black/50 text-white"}`;

export default function PhotoEditor({ file, onCancel, onSend }) {
  const [base, setBase] = useState(null);
  const [strokes, setStrokes] = useState([]);
  const [texts, setTexts] = useState([]);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState(null);
  const [color, setColor] = useState(COLORS[2]);
  const [editing, setEditing] = useState(null);
  const [crop, setCrop] = useState(FULL);
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(null);

  useEffect(() => {
    let alive = true;
    const url = URL.createObjectURL(file);
    loadImage(url)
      .then((img) => { if (alive) setBase(fitCanvas(img)); })
      .catch(() => { if (alive) { toast.error("Couldn't open the photo"); onCancel(); } });
    return () => { alive = false; URL.revokeObjectURL(url); };
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  // Android back closes the text overlay / tool first, then the editor.
  const backRef = useRef(null);
  backRef.current = () => (editing ? setEditing(null) : mode ? setMode(null) : !sending && onCancel());
  useEffect(() => {
    const sub = App.addListener("backButton", () => backRef.current());
    return () => { sub.then((h) => h.remove()); };
  }, []);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!base || !el) return;
    const fit = () => {
      const s = Math.min(el.clientWidth / base.width, el.clientHeight / base.height);
      setBox({ w: base.width * s, h: base.height * s });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [base]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!base || !c) return;
    c.width = base.width;
    c.height = base.height;
    const ctx = c.getContext("2d");
    ctx.drawImage(base, 0, 0);
    strokes.forEach((s) => drawStroke(ctx, s));
  }, [base, strokes]);

  const snapshot = () => setHistory((h) => [...h, { base, strokes, texts }]);
  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setBase(last.base); setStrokes(last.strokes); setTexts(last.texts);
    setHistory((h) => h.slice(0, -1));
  };
  const bake = (fn) => {
    snapshot();
    setBase(fn(flatten(base, strokes, texts)));
    setStrokes([]); setTexts([]);
  };

  const toImg = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * base.width, ((e.clientY - r.top) / r.height) * base.height];
  };
  const penDown = (e) => {
    if (mode !== "draw") return;
    e.currentTarget.setPointerCapture(e.pointerId);
    snapshot();
    drawing.current = { color, width: base.width * 0.012, points: [toImg(e)] };
    drawStroke(canvasRef.current.getContext("2d"), drawing.current);
  };
  const penMove = (e) => {
    const s = drawing.current;
    if (!s) return;
    const p = toImg(e);
    const ctx = canvasRef.current.getContext("2d");
    drawStroke(ctx, { ...s, points: [s.points[s.points.length - 1], p] });
    s.points.push(p);
  };
  const penUp = () => {
    const s = drawing.current;
    drawing.current = null;
    if (s) setStrokes((prev) => [...prev, s]);
  };

  const finishText = (t) => {
    setEditing(null);
    const exists = texts.some((x) => x.id === t.id);
    if (!t.text && !exists) return;
    snapshot();
    setTexts((prev) => (!t.text ? prev.filter((x) => x.id !== t.id) : exists ? prev.map((x) => (x.id === t.id ? t : x)) : [...prev, t]));
  };
  const newText = () => { setMode(null); setEditing({ id: Date.now().toString(36), text: "", color: COLORS[0], size: 0.08, x: 0.5, y: 0.5 }); };

  const send = async () => {
    setSending(true);
    try {
      await onSend(await canvasToFile(flatten(base, strokes, texts)), caption.trim());
    } catch {
      toast.error("Couldn't send the photo");
      setSending(false);
    }
  };

  const cropping = mode === "crop";
  return createPortal(
    <div data-testid="photo-editor" className="fixed inset-0 z-[200] bg-black flex flex-col select-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center gap-2 px-3 py-3">
        <button type="button" data-testid="photo-editor-close-btn" aria-label="Discard photo" onClick={onCancel} disabled={sending} className={toolBtn(false)}><X className="h-5 w-5" /></button>
        <button type="button" data-testid="photo-editor-undo-btn" aria-label="Undo" onClick={undo} disabled={!history.length || cropping || sending} className={toolBtn(false)}><Undo2 className="h-5 w-5" /></button>
        <div className="flex-1" />
        <button type="button" data-testid="photo-editor-crop-btn" aria-label="Crop" disabled={!base || sending} onClick={() => { setCrop(FULL); setMode(cropping ? null : "crop"); }} className={toolBtn(cropping)}><Crop className="h-5 w-5" /></button>
        <button type="button" data-testid="photo-editor-rotate-btn" aria-label="Rotate" disabled={!base || cropping || sending} onClick={() => bake(rotateCanvas)} className={toolBtn(false)}><RotateCw className="h-5 w-5" /></button>
        <button type="button" data-testid="photo-editor-draw-btn" aria-label="Draw" disabled={!base || cropping || sending} onClick={() => setMode(mode === "draw" ? null : "draw")} className={toolBtn(mode === "draw")}><Pencil className="h-5 w-5" /></button>
        <button type="button" data-testid="photo-editor-text-btn" aria-label="Add text" disabled={!base || cropping || sending} onClick={newText} className={toolBtn(false)}><Type className="h-5 w-5" /></button>
      </div>

      <div ref={stageRef} className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden">
        {!base && <Loader2 className="h-8 w-8 animate-spin text-white/60" />}
        {base && (
          <div className="relative" style={{ width: box.w, height: box.h }}>
            <canvas ref={canvasRef} data-testid="photo-editor-canvas" onPointerDown={penDown} onPointerMove={penMove} onPointerUp={penUp} onPointerCancel={penUp}
              className={`block h-full w-full touch-none ${mode === "draw" ? "cursor-crosshair" : ""}`} />
            {!cropping && texts.map((t) => (
              <TextItem key={t.id} t={t} box={box} interactive={!mode} onTap={setEditing} onDragStart={snapshot}
                onMove={(id, x, y) => setTexts((prev) => prev.map((p) => (p.id === id ? { ...p, x, y } : p)))} />
            ))}
            {cropping && <CropBox rect={crop} onChange={setCrop} />}
          </div>
        )}
      </div>

      <div className="px-4 pt-3 pb-4 space-y-3">
        <div className={mode === "draw" ? "" : "invisible"} aria-hidden={mode !== "draw"}>
          <ColorRow value={color} onChange={setColor} testid="photo-pen-color" />
        </div>
        {cropping ? (
          <div className="flex justify-center gap-3">
            <button type="button" data-testid="photo-crop-cancel-btn" onClick={() => setMode(null)} className="rounded-full bg-[#27272A] px-5 py-2.5 text-sm text-white">Cancel</button>
            <button type="button" data-testid="photo-crop-apply-btn" onClick={() => { bake((c) => cropCanvas(c, crop)); setMode(null); }}
              className="rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white flex items-center gap-1.5"><Check className="h-4 w-4" /> Apply crop</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input data-testid="photo-editor-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a caption…" disabled={sending}
              className="flex-1 min-w-0 rounded-full bg-[#1E1E1E] border border-[#27272A] px-4 py-3 text-[15px] text-[#F3F4F6] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#3B82F6]/60" />
            <button type="button" data-testid="photo-editor-send-btn" aria-label="Send photo" onClick={send} disabled={!base || sending}
              className="h-12 w-12 shrink-0 rounded-full bg-[#2563EB] text-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-40">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </div>
        )}
      </div>

      {editing && <TextInput initial={editing} boxW={box.w} onDone={finishText} />}
    </div>,
    document.body,
  );
}
